import { ChangeEvent, InputHTMLAttributes, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { UploadIcon } from "../../../assets/svg";
import { FaCheckCircle } from "react-icons/fa";

const fileSizeLimit = 10 * 1024 * 1024;
const fileAllowedExtensions = ["png", "jpg", "jpeg", "pdf"];
const accept = "image/jpeg, image/png, application/pdf";

interface InputTextProps<FieldsType extends FieldValues> {
  name: Path<FieldsType>;
  control: Control<FieldsType>;
  inputProps?: InputHTMLAttributes<HTMLInputElement>;
  label?: string;
  shouldUnregister?: boolean;
  placeholder?: string;
  note?: string;
  isMultiple?: boolean;
  onFileSelect: (file: File[], callBack?: () => void) => void;
  rules?: RegisterOptions<FieldsType>;
  minFile?: number;
}

export default function FileUploader<FieldsType extends FieldValues>({
  control,
  name,
  label,
  shouldUnregister,
  isMultiple,
  onFileSelect,
  placeholder,
  rules,
  minFile = 1,
}: InputTextProps<FieldsType>) {
  const { t } = useTranslation();
  const [showTickMark, setShowTickMark] = useState(false);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { files: inputFiles },
    } = event;
    event.persist();
    if (!inputFiles) {
      return;
    }

    const fileArray: File[] = Array.from(inputFiles);

    for (const idx in fileArray) {
      const file = fileArray[idx];
      if (fileSizeLimit && file.size > fileSizeLimit) {
        toast.error(
          `File is too big. Should be less then ${
            fileSizeLimit / (1024 * 1024)
          } MB`,
          {
            position: "bottom-center",
          }
        );
        event.target.value = "";
        return;
      }
      const fileName = file.name;
      const fileExtension = fileName.substring(
        fileName.lastIndexOf(".") + 1,
        fileName?.length
      );
      if (!fileAllowedExtensions.includes(fileExtension.toLowerCase())) {
        toast.error(`File ${file.name} is not supported`, {
          position: "bottom-center",
        });
        event.target.value = "";
        return;
      }
    }

    if (fileArray && fileArray.length > 0) {
      onFileSelect(fileArray, () => setShowTickMark(true));
    }
    event.target.value = "";
  };

  return (
    <Controller
      name={name}
      control={control}
      shouldUnregister={shouldUnregister}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <div className="py-2 space-y-1">
          <div className="flex justify-between items-center gap-3">
            <p>
              {t(label)}
              <span className="text-red-600 ml-0.5 text-base">*</span>
            </p>
            {showTickMark && (
              <span className="inline-block ">
                <FaCheckCircle className="text-green-600 text-lg" />
              </span>
            )}
          </div>
          <label
            htmlFor={field.name}
            className="bg-[#2D6CCA14] w-full cursor-pointer  border-[#B5C2D1] border-2 border-dashed rounded flex gap-2 justify-center items-center p-2 relative"
          >
            <UploadIcon size={30} className="stroke-[1.5px]" />
            <span className="text-[#2D6CCA] text-center">
              {t(placeholder || "Upload you file..")}
            </span>
            <input
              type="file"
              id={field.name}
              multiple={isMultiple}
              {...field}
              onChange={(e) => {
                handleFileChange(e);
                field.onChange(e);
              }}
              className="hidden"
              accept={accept}
            />
          </label>
          <p className="text-red-600 text-left mt-0.5 font-normal text-sm m-0 p-0  bottom-0 left-0 right-0">
            {error?.message}
          </p>
        </div>
      )}
    />
  );
}
